import { config } from '../../utils/utils';
import store from '../../store';
import { toastHandler } from '../../utils/toastHandler';

// Types
export interface AgentCredits {
  allocatedCredits: number;
  usedCredits: number;
  remainingCredits: number;
}

export interface ReduceCreditsRequest {
  credits: number;
}

export interface ReduceCreditsResponse {
  success: boolean;
  agentCredits: {
    allocatedCredits: number;
    usedCredits: number;
    remainingCredits: number;
  };
  companyWallet: {
    remainingCredits: number;
  };
}

/**
 * Get agent credits
 */
export const getAgentCredits = async (): Promise<AgentCredits> => {
  try {
    const { accessToken } = store.getState().auth;

    const response = await fetch(`${config.BASE_URL}/agent-credits`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to fetch agent credits');
    }

    return await response.json();
  } catch (error: any) {
    toastHandler(error);
    throw error;
  }
};

/**
 * Reduce credits from an agent (company admin only)
 */
export const reduceAgentCredits = async (
  agentId: string,
  data: ReduceCreditsRequest
): Promise<ReduceCreditsResponse> => {
  try {
    const { accessToken } = store.getState().auth;

    // Validate agentId
    if (!agentId || typeof agentId !== 'string') {
      throw new Error('Invalid agent ID');
    }

    // Validate data
    if (!data || typeof data.credits !== 'number' || data.credits <= 0) {
      throw new Error('Invalid credits amount');
    }

    const url = `${config.BASE_URL}/agent-credits/${encodeURIComponent(agentId)}/reduce`;
    
    console.log('[reduceAgentCredits] Request:', {
      url,
      method: 'PATCH',
      agentId,
      credits: data.credits,
    });

    const response = await fetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('[reduceAgentCredits] Error response:', {
        status: response.status,
        statusText: response.statusText,
        errorData,
      });
      throw new Error(errorData.message || `Failed to reduce agent credits: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();
    console.log('[reduceAgentCredits] Success:', result);
    return result;
  } catch (error: any) {
    console.error('[reduceAgentCredits] Error:', error);
    toastHandler(error);
    throw error;
  }
};

